// =======================================================
// AttractionsEditor — Nearby Attractions Manager
// File: src/views/pages/booking-engine/admin/AttractionsEditor.jsx
// Manages nearby attractions / points of interest shown on
// the booking engine. Follows the same data contract as
// amenities (items array inside branding.attractions).
// =======================================================

import React, { useState } from "react";
import {
  Form,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Modal,
  Alert,
} from "react-bootstrap";

// =============================
// DEFAULT ATTRACTIONS DIRECTORY
// =============================

const DEFAULT_ATTRACTIONS = [
  {
    key: "railwayStation",
    label: "Railway Station",
    icon: "🚂",
    defaultDescription: "Nearest railway station with regular connectivity to major cities.",
    defaultDistance: "",
  },
  {
    key: "airport",
    label: "Airport",
    icon: "✈️",
    defaultDescription: "Nearest airport with domestic and international connectivity.",
    defaultDistance: "",
  },
  {
    key: "busStand",
    label: "Bus Stand",
    icon: "🚌",
    defaultDescription: "Central bus stand with inter-city and local bus services.",
    defaultDistance: "",
  },
  {
    key: "temple",
    label: "Temple / Religious Site",
    icon: "🛕",
    defaultDescription: "Popular temple or religious site frequently visited by guests.",
    defaultDistance: "",
  },
  {
    key: "beach",
    label: "Beach",
    icon: "🏖️",
    defaultDescription: "Nearest beach with public access.",
    defaultDistance: "",
  },
  {
    key: "market",
    label: "Local Market / Shopping Area",
    icon: "🛍️",
    defaultDescription: "Popular local market for shopping, souvenirs, and street food.",
    defaultDistance: "",
  },
  {
    key: "museum",
    label: "Museum / Heritage Site",
    icon: "🏛️",
    defaultDescription: "Museum or heritage site of historical significance.",
    defaultDistance: "",
  },
  {
    key: "hospital",
    label: "Hospital / Medical Facility",
    icon: "🏥",
    defaultDescription: "Nearest hospital or medical facility for emergencies.",
    defaultDistance: "",
  },
  {
    key: "mall",
    label: "Shopping Mall",
    icon: "🏬",
    defaultDescription: "Modern shopping mall with retail stores, dining, and entertainment.",
    defaultDistance: "",
  },
  {
    key: "park",
    label: "Park / Garden",
    icon: "🌳",
    defaultDescription: "Public park or botanical garden, ideal for morning walks.",
    defaultDistance: "",
  },
  {
    key: "lake",
    label: "Lake / River",
    icon: "🏞️",
    defaultDescription: "Scenic lake or river offering boating and waterside walks.",
    defaultDistance: "",
  },
  {
    key: "fort",
    label: "Fort / Palace",
    icon: "🏰",
    defaultDescription: "Historic fort or palace, a must-visit landmark.",
    defaultDistance: "",
  },
  {
    key: "hillPoint",
    label: "Hill Point / Viewpoint",
    icon: "⛰️",
    defaultDescription: "Popular viewpoint offering panoramic views of the surrounding area.",
    defaultDistance: "",
  },
  {
    key: "waterfall",
    label: "Waterfall",
    icon: "💧",
    defaultDescription: "Scenic waterfall, a popular attraction especially during monsoon.",
    defaultDistance: "",
  },
  {
    key: "amusementPark",
    label: "Amusement / Theme Park",
    icon: "🎢",
    defaultDescription: "Family-friendly amusement or theme park.",
    defaultDistance: "",
  },
  {
    key: "wildlife",
    label: "Wildlife Sanctuary / Zoo",
    icon: "🦁",
    defaultDescription: "Wildlife sanctuary or zoo with guided tours.",
    defaultDistance: "",
  },
  {
    key: "golfCourse",
    label: "Golf Course",
    icon: "⛳",
    defaultDescription: "Nearby golf course open to visitors.",
    defaultDistance: "",
  },
  {
    key: "metroStation",
    label: "Metro Station",
    icon: "🚇",
    defaultDescription: "Nearest metro station for quick city transit.",
    defaultDistance: "",
  },
];

// =============================
// DISTANCE UNIT OPTIONS
// =============================
const DISTANCE_UNITS = ["km", "mi", "min walk", "min drive"];

// =============================
// COMPONENT
// =============================

export default function AttractionsEditor({ branding, setBranding }) {
  // ------- local UI state -------
  const [selectedDefaultKey, setSelectedDefaultKey] = useState("");
  const [newAttractionName, setNewAttractionName] = useState("");

  // editor modal
  const [editingKey, setEditingKey] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [draft, setDraft] = useState({
    label: "",
    description: "",
    distance: "",
    distanceUnit: "km",
    icon: "",
  });

  // ------- section helpers (mirror PropertyContentEditor pattern) -------
  const createDefaultSection = () => ({
    enabled: true,
    title: "Nearby Attractions",
    items: [],
  });

  const getSection = () => branding?.attractions || createDefaultSection();

  const updateSection = (updater) => {
    setBranding((prev) => {
      const base = prev?.attractions || createDefaultSection();
      const updated = updater(base);
      return { ...prev, attractions: updated };
    });
  };

  const section = getSection();

  // sorted items
  const itemsSorted = (section.items || [])
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const existingKeys = itemsSorted.map((a) => a.key);
  const availableDefaults = DEFAULT_ATTRACTIONS.filter(
    (d) => !existingKeys.includes(d.key)
  );

  // ------- item CRUD -------
  const normalizeOrder = (items) => items.map((it, i) => ({ ...it, order: i }));

  const updateItemByKey = (key, patch) => {
    updateSection((sec) => {
      const items = (sec.items || []).map((it) =>
        it.key === key ? { ...it, ...patch } : it
      );
      return { ...sec, items };
    });
  };

  const removeItemByKey = (key) => {
    updateSection((sec) => {
      const filtered = (sec.items || []).filter((it) => it.key !== key);
      return { ...sec, items: normalizeOrder(filtered) };
    });
  };

  const moveItem = (key, direction) => {
    updateSection((sec) => {
      const sorted = (sec.items || [])
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const idx = sorted.findIndex((it) => it.key === key);
      if (idx < 0) return sec;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= sorted.length) return sec;
      const arr = [...sorted];
      const tmp = arr[idx];
      arr[idx] = arr[newIdx];
      arr[newIdx] = tmp;
      return { ...sec, items: normalizeOrder(arr) };
    });
  };

  // ------- add default attraction -------
  const handleAddDefault = () => {
    if (!selectedDefaultKey) return;
    const def = DEFAULT_ATTRACTIONS.find((d) => d.key === selectedDefaultKey);
    if (!def) return;

    updateSection((sec) => {
      const items = [...(sec.items || [])];
      items.push({
        key: def.key,
        label: def.label,
        icon: def.icon || "",
        enabled: true,
        description: def.defaultDescription || "",
        distance: def.defaultDistance || "",
        distanceUnit: "km",
        source: "default",
        order: items.length,
      });
      return { ...sec, items };
    });
    setSelectedDefaultKey("");
  };

  // ------- add custom attraction -------
  const handleAddCustom = () => {
    const name = (newAttractionName || "").trim();
    if (!name) return;

    updateSection((sec) => {
      const items = [...(sec.items || [])];
      items.push({
        key: `custom-${Date.now()}`,
        label: name,
        icon: "📍",
        enabled: true,
        description: "",
        distance: "",
        distanceUnit: "km",
        source: "custom",
        order: items.length,
      });
      return { ...sec, items };
    });
    setNewAttractionName("");
  };

  // ------- editor modal helpers -------
  const getDefaultByKey = (key) =>
    DEFAULT_ATTRACTIONS.find((x) => String(x.key) === String(key)) || null;

  const openEditor = (key) => {
    if (!key) return;
    const current = itemsSorted.find((x) => x.key === key) || null;
    const def = getDefaultByKey(key);

    setEditingKey(key);
    setDraft({
      label: (current?.label ?? def?.label ?? "").toString(),
      description: (current?.description ?? def?.defaultDescription ?? "").toString(),
      distance: (current?.distance ?? def?.defaultDistance ?? "").toString(),
      distanceUnit: current?.distanceUnit || "km",
      icon: (current?.icon ?? def?.icon ?? "📍").toString(),
    });
    setShowEditModal(true);
  };

  const closeEditor = () => {
    setShowEditModal(false);
    setEditingKey(null);
    setDraft({ label: "", description: "", distance: "", distanceUnit: "km", icon: "" });
  };

  const saveEditor = () => {
    if (!editingKey) return;
    updateItemByKey(editingKey, {
      label: String(draft.label || "").trim(),
      description: String(draft.description || ""),
      distance: String(draft.distance || "").trim(),
      distanceUnit: draft.distanceUnit || "km",
      icon: String(draft.icon || "📍").trim(),
    });
    closeEditor();
  };

  const resetToDefault = () => {
    if (!editingKey) return;
    const def = getDefaultByKey(editingKey);
    if (!def) return;
    setDraft((p) => ({
      ...p,
      description: String(def.defaultDescription || ""),
      distance: String(def.defaultDistance || ""),
    }));
  };

  const currentEditing = editingKey
    ? itemsSorted.find((a) => a.key === editingKey)
    : null;

  // ------- section header (same pattern as main editor) -------
  const renderSectionHeader = () => (
    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
      <div className="d-flex flex-column flex-sm-row gap-2 align-items-start align-items-sm-center">
        <Form.Label className="mb-0 fw-semibold">Nearby Attractions</Form.Label>
        <Badge bg="light" text="dark">
          Points of interest shown to guests
        </Badge>
      </div>
      <Form.Check
        type="switch"
        id="attractions-enabled"
        label={
          section.enabled
            ? "Visible on booking engine"
            : "Hidden on booking engine"
        }
        checked={section.enabled}
        onChange={(e) =>
          updateSection((sec) => ({ ...sec, enabled: e.target.checked }))
        }
      />
    </div>
  );

  // =============================
  // RENDER
  // =============================
  return (
    <div className="pt-3">
      {renderSectionHeader()}

      {!section.enabled ? (
        <div className="text-muted small">
          Turn this section <strong>On</strong> to manage and show nearby attractions.
        </div>
      ) : (
        <>
          {/* Section Title */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Section Title</Form.Label>
                <Form.Control
                  type="text"
                  value={section.title || "Nearby Attractions"}
                  onChange={(e) =>
                    updateSection((sec) => ({ ...sec, title: e.target.value }))
                  }
                  placeholder="e.g. Nearby Attractions & Points of Interest"
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Quick Add */}
          <Card className="mb-3 border-0 shadow-sm">
            <Card.Body>
              <div className="mb-2 fw-semibold">Quick add attractions</div>
              <Row className="gy-2">
                <Col md={6}>
                  <Form.Label className="small">Add from common attractions</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Select
                      size="sm"
                      value={selectedDefaultKey}
                      onChange={(e) => setSelectedDefaultKey(e.target.value)}
                    >
                      <option value="">Choose an attraction...</option>
                      {availableDefaults.map((a) => (
                        <option key={a.key} value={a.key}>
                          {a.icon} {a.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      disabled={!selectedDefaultKey}
                      onClick={handleAddDefault}
                    >
                      Add
                    </Button>
                  </div>
                  <small className="text-muted">
                    Common nearby landmarks &amp; points of interest.
                  </small>
                </Col>

                <Col md={6}>
                  <Form.Label className="small">Add custom attraction</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control
                      size="sm"
                      type="text"
                      placeholder="e.g. City Palace, Sunset Point"
                      value={newAttractionName}
                      onChange={(e) => setNewAttractionName(e.target.value)}
                    />
                    <Button
                      size="sm"
                      variant="outline-success"
                      disabled={!newAttractionName.trim()}
                      onClick={handleAddCustom}
                    >
                      Add
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Attractions Table */}
          <div className="mb-2 fw-semibold">Your attractions</div>

          {itemsSorted.length === 0 ? (
            <div className="text-muted small mb-2">
              You have not added any attractions yet. Use the quick add above or
              create your own.
            </div>
          ) : (
            <small className="text-muted d-block mb-2">
              Toggle <strong>Show</strong> to control visibility. Click{" "}
              <strong>Edit</strong> to update description &amp; distance.
            </small>
          )}

          {itemsSorted.length === 0 ? null : (
            <Table hover responsive size="sm" className="align-middle">
              <thead>
                <tr>
                  <th style={{ width: "5rem" }}>Show</th>
                  <th style={{ width: "2.5rem" }}></th>
                  <th>Name</th>
                  <th style={{ width: "22%" }}>Distance</th>
                  <th style={{ width: "28%" }}>Description</th>
                  <th style={{ width: "6rem" }}>Type</th>
                  <th style={{ width: "80px" }}>Order</th>
                  <th style={{ width: "7rem" }}></th>
                </tr>
              </thead>
              <tbody>
                {itemsSorted.map((a, idx) => {
                  const k = a?.key ? String(a.key) : "";
                  if (!k) return null;

                  const isFirst = idx === 0;
                  const isLast = idx === itemsSorted.length - 1;

                  const descPreview = String(a?.description || "").trim();
                  const shortDesc =
                    descPreview.length > 60
                      ? `${descPreview.slice(0, 60)}…`
                      : descPreview;

                  const distLabel = a.distance
                    ? `${a.distance} ${a.distanceUnit || "km"}`
                    : "—";

                  return (
                    <tr key={k}>
                      {/* Toggle */}
                      <td>
                        <Form.Check
                          type="switch"
                          id={`attraction-${k}`}
                          checked={!!a.enabled}
                          onChange={(e) =>
                            updateItemByKey(k, { enabled: e.target.checked })
                          }
                          label=""
                        />
                      </td>

                      {/* Icon */}
                      <td className="text-center" style={{ fontSize: "1.15rem" }}>
                        {a.icon || "📍"}
                      </td>

                      {/* Name */}
                      <td>
                        <Form.Control
                          size="sm"
                          type="text"
                          value={a.label || ""}
                          onChange={(e) =>
                            updateItemByKey(k, { label: e.target.value })
                          }
                          placeholder="Attraction name"
                        />
                      </td>

                      {/* Distance */}
                      <td>
                        <div className="d-flex gap-1">
                          <Form.Control
                            size="sm"
                            type="text"
                            value={a.distance || ""}
                            onChange={(e) =>
                              updateItemByKey(k, { distance: e.target.value })
                            }
                            placeholder="e.g. 10"
                            style={{ width: "60px" }}
                          />
                          <Form.Select
                            size="sm"
                            value={a.distanceUnit || "km"}
                            onChange={(e) =>
                              updateItemByKey(k, { distanceUnit: e.target.value })
                            }
                            style={{ width: "100px" }}
                          >
                            {DISTANCE_UNITS.map((u) => (
                              <option key={u} value={u}>
                                {u}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                      </td>

                      {/* Description */}
                      <td>
                        <div className="small text-muted" style={{ lineHeight: 1.25 }}>
                          {shortDesc || <em>No description</em>}
                        </div>
                      </td>

                      {/* Type */}
                      <td>
                        <Badge bg="light" text="dark">
                          {a.source === "custom" ? "Custom" : "Default"}
                        </Badge>
                      </td>

                      {/* Order */}
                      <td>
                        <div className="d-flex flex-column gap-1">
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={isFirst}
                            onClick={() => moveItem(k, -1)}
                          >
                            ↑
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={isLast}
                            onClick={() => moveItem(k, +1)}
                          >
                            ↓
                          </Button>
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="text-end">
                        <div className="d-flex gap-1 justify-content-end">
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => openEditor(k)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => {
                              if (editingKey === k) closeEditor();
                              removeItemByKey(k);
                            }}
                            title="Remove"
                          >
                            ✕
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </>
      )}

      {/* ===== EDIT ATTRACTION MODAL ===== */}
      <Modal
        show={showEditModal}
        onHide={closeEditor}
        centered
        size="lg"
        scrollable
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Edit attraction
            {currentEditing?.label ? `: ${currentEditing.label}` : ""}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row className="gy-3">
            <Col md={7}>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  value={draft.label}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, label: e.target.value }))
                  }
                  placeholder="Attraction name..."
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Icon (emoji)</Form.Label>
                <Form.Control
                  type="text"
                  value={draft.icon}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, icon: e.target.value }))
                  }
                  placeholder="e.g. 🚂, 🏖️, ⛰️"
                  style={{ width: "80px" }}
                />
                <small className="text-muted">
                  Single emoji shown beside the attraction name.
                </small>
              </Form.Group>

              <Row>
                <Col sm={5}>
                  <Form.Group className="mb-3">
                    <Form.Label>Distance</Form.Label>
                    <Form.Control
                      type="text"
                      value={draft.distance}
                      onChange={(e) =>
                        setDraft((p) => ({ ...p, distance: e.target.value }))
                      }
                      placeholder="e.g. 10"
                    />
                  </Form.Group>
                </Col>
                <Col sm={5}>
                  <Form.Group className="mb-3">
                    <Form.Label>Unit</Form.Label>
                    <Form.Select
                      value={draft.distanceUnit}
                      onChange={(e) =>
                        setDraft((p) => ({ ...p, distanceUnit: e.target.value }))
                      }
                    >
                      {DISTANCE_UNITS.map((u) => (
                        <option key={u} value={u}>
                          {u}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-2">
                <Form.Label>Description / note</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={draft.description}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, description: e.target.value }))
                  }
                  placeholder="Short note guests will see..."
                />

                <div className="d-flex justify-content-between align-items-center mt-2">
                  <small className="text-muted">
                    Keep it brief. Guests scan quickly.
                  </small>

                  {editingKey && getDefaultByKey(editingKey)?.defaultDescription ? (
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={resetToDefault}
                    >
                      Reset to default
                    </Button>
                  ) : null}
                </div>
              </Form.Group>
            </Col>

            {/* Preview */}
            <Col md={5}>
              <div className="fw-semibold mb-2">Preview</div>
              <Card className="border-0 shadow-sm">
                <Card.Body style={{ maxHeight: 340, overflow: "auto" }}>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span style={{ fontSize: "1.4rem" }}>
                      {draft.icon?.trim() || "📍"}
                    </span>
                    <div className="fw-semibold">
                      {draft.label?.trim() || "Attraction name"}
                    </div>
                  </div>

                  {draft.distance?.trim() && (
                    <Badge bg="info" className="mb-2">
                      {draft.distance} {draft.distanceUnit || "km"} from property
                    </Badge>
                  )}

                  <div
                    className="text-muted small mt-2"
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    {draft.description?.trim() || "No description"}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Alert variant="light" className="mt-3 mb-0 small">
            Tip: Include distance so guests can plan their itinerary. Keep
            descriptions to 1–2 lines.
          </Alert>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeEditor}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveEditor}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}